const W = 1080, H = 1920, END_SECONDS = 3.5;
const TERRA = "#D95A40", INK = "#1A1A18";
const DISPLAY = "'Cabinet Grotesk', sans-serif", SANS = "'Manrope', sans-serif", MONO = "'JetBrains Mono', monospace";

export const pickClipMime = () =>
  ["video/mp4;codecs=avc1.42E01E,mp4a.40.2", "video/mp4;codecs=avc1,mp4a", "video/mp4", "video/webm;codecs=vp9,opus", "video/webm"]
    .find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";

const roundRect = (c, x, y, w, h, r) => { c.beginPath(); c.roundRect(x, y, w, h, r); c.fill(); };

const wrap = (c, text, maxW) => {
  const lines = []; let line = "";
  text.split(/\s+/).forEach((w) => {
    const t = line ? `${line} ${w}` : w;
    if (c.measureText(t).width > maxW && line) { lines.push(line); line = w; } else line = t;
  });
  if (line) lines.push(line);
  return lines;
};

function drawLogo(c, x, y, s, light = true, inverse = false) {
  c.fillStyle = inverse ? "#fff" : TERRA; roundRect(c, x, y, s, s, s * 0.12);
  const cx = x + s / 2, cy = y + s / 2, r = s * 0.32, fg = inverse ? TERRA : "#fff";
  c.strokeStyle = fg; c.lineWidth = s * 0.07; c.beginPath(); c.arc(cx, cy, r, 0, Math.PI * 2); c.stroke();
  c.fillStyle = fg; c.beginPath();
  c.moveTo(cx + r * 0.55, cy - r * 0.55); c.lineTo(cx + r * 0.2, cy + r * 0.2); c.lineTo(cx - r * 0.55, cy + r * 0.55); c.lineTo(cx - r * 0.2, cy - r * 0.2);
  c.closePath(); c.fill();
  c.fillStyle = light ? "#fff" : INK; c.textAlign = "left"; c.textBaseline = "alphabetic";
  c.font = `800 ${s * 0.36}px ${DISPLAY}`; c.fillText("RESPONSIBLE ADVENTURES", x + s * 1.25, y + s * 0.52);
  c.globalAlpha = 0.75; c.font = `500 ${s * 0.2}px ${MONO}`; c.fillText("BEYOND THE ORDINARY", x + s * 1.27, y + s * 0.86); c.globalAlpha = 1;
}

const stars = (n) => "★".repeat(n || 5);

function drawFrame(c, video, review, caption) {
  c.fillStyle = "#000"; c.fillRect(0, 0, W, H);
  const vw = video.videoWidth || W, vh = video.videoHeight || H, sc = Math.max(W / vw, H / vh);
  c.drawImage(video, (W - vw * sc) / 2, (H - vh * sc) / 2, vw * sc, vh * sc);
  let g = c.createLinearGradient(0, 0, 0, 360); g.addColorStop(0, "rgba(0,0,0,0.65)"); g.addColorStop(1, "rgba(0,0,0,0)");
  c.fillStyle = g; c.fillRect(0, 0, W, 360);
  g = c.createLinearGradient(0, H - 760, 0, H); g.addColorStop(0, "rgba(0,0,0,0)"); g.addColorStop(1, "rgba(0,0,0,0.85)");
  c.fillStyle = g; c.fillRect(0, H - 760, W, 760);
  drawLogo(c, 64, 84, 96);

  if (caption) {
    c.font = `700 54px ${SANS}`; c.textAlign = "center";
    const lines = wrap(c, caption, W - 220).slice(-3), lh = 72, bh = lines.length * lh + 44, by = H - 560 - bh;
    c.fillStyle = "rgba(0,0,0,0.6)"; roundRect(c, 70, by, W - 140, bh, 28);
    c.fillStyle = "#fff"; c.textBaseline = "middle";
    lines.forEach((l, i) => c.fillText(l, W / 2, by + 22 + lh * i + lh / 2));
  }
  c.textAlign = "left"; c.textBaseline = "alphabetic";
  c.fillStyle = TERRA; c.font = `400 56px ${SANS}`; c.fillText(stars(review.rating), 72, H - 330);
  c.fillStyle = "#fff"; c.font = `800 96px ${DISPLAY}`; c.fillText(review.first_name || review.client_name, 72, H - 220);
  if (review.trip_name) {
    c.globalAlpha = 0.8; c.font = `500 34px ${MONO}`;
    c.fillText(wrap(c, review.trip_name.toUpperCase(), W - 150)[0], 76, H - 150); c.globalAlpha = 1;
  }
}

function drawEnd(c, review, endText, t) {
  c.globalAlpha = 1; c.fillStyle = TERRA; c.fillRect(0, 0, W, H);
  c.globalAlpha = Math.min(1, t / 0.5);
  drawLogo(c, 150, 640, 120, true, true);
  c.fillStyle = "#fff"; c.textAlign = "center"; c.font = `400 64px ${SANS}`; c.fillText(stars(review.rating), W / 2, 960);
  c.font = `800 82px ${DISPLAY}`;
  wrap(c, endText, W - 180).forEach((l, i) => c.fillText(l, W / 2, 1110 + i * 100));
  c.globalAlpha = 1;
}

const activeCaption = (segs, t) => segs.find((s) => t >= s.start && t <= s.end + 0.25)?.text || "";

export function primeClip(videoUrl) {
  const video = document.createElement("video");
  video.crossOrigin = "anonymous"; video.playsInline = true; video.preload = "auto"; video.src = videoUrl;
  const actx = new (window.AudioContext || window.webkitAudioContext)();
  const dest = actx.createMediaStreamDestination();
  actx.createMediaElementSource(video).connect(dest);
  const ready = Promise.all([actx.resume(), video.play().then(() => { video.pause(); video.currentTime = 0; })]);
  ready.catch(() => {});
  return { video, actx, dest, ready };
}

export async function renderSocialClip({ prime, review, segments = [], endText, onProgress }) {
  const { video, actx, dest } = prime;
  await prime.ready;
  await document.fonts?.ready;
  const canvas = document.createElement("canvas"); canvas.width = W; canvas.height = H;
  const c = canvas.getContext("2d");
  if (video.readyState < 3) await new Promise((res, rej) => { video.oncanplay = res; video.onerror = () => rej(new Error("Couldn't load the review video")); });
  const stream = new MediaStream([...canvas.captureStream(30).getVideoTracks(), ...dest.stream.getAudioTracks()]);
  const mime = pickClipMime();
  const rec = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: 8_000_000 });
  const chunks = [];
  rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
  const stopped = new Promise((res) => { rec.onstop = res; });

  drawFrame(c, video, review, "");
  rec.start(500);
  await video.play();
  const dur = video.duration && Number.isFinite(video.duration) ? video.duration : 60;
  let endStart = null, raf;
  await new Promise((resolve) => {
    const tick = (now) => {
      if (!video.ended && endStart === null) {
        drawFrame(c, video, review, activeCaption(segments, video.currentTime));
        onProgress?.(Math.min(0.9, (video.currentTime / dur) * 0.9));
      } else {
        endStart = endStart ?? now;
        const t = (now - endStart) / 1000;
        drawEnd(c, review, endText, t);
        onProgress?.(0.9 + Math.min(0.1, (t / END_SECONDS) * 0.1));
        if (t >= END_SECONDS) return resolve();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  });
  cancelAnimationFrame(raf);
  rec.stop(); await stopped;
  stream.getTracks().forEach((t) => t.stop()); actx.close();
  const type = (rec.mimeType || mime || "video/webm").split(";")[0];
  return new Blob(chunks, { type });
}
